import React, { useEffect, Fragment, useState, useRef } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useForm, useFieldArray, useWatch } from "react-hook-form";
import { showToast } from "../../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import Breadcrumb from "../../../../components/common/breadcrumb";
import { GetIpdEntries } from "../../../../api/ipd/ipdEntryAPI";

// Validation schema (basic example — you can expand this)
import { StoreOxygenGiven } from "../../../../api/ipd/oxygenGivenAPI";
import useKeyboardSubmit from "../../../../hooks/useKeyboardSubmit";
import useEnterNavigation from "../../../../hooks/useEnterNavigation";

const schema = yup.object().shape({
    ipdEntryId: yup.string().required("IPD Entry is required"),
    oxygenGivenDetails: yup.array().of(
        yup.object().shape({
            date: yup.string().required("Date is required"),
            startTime: yup.string().required("Start time is required"),
            endTime: yup.string().required("End time is required"),
        })
    ),
});


const AddOxygenGivenEntry = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [ipdEntries, setIpdEntries] = useState([]);
    const [patientDetails, setPatientDetails] = useState({});

    const {
        register,
        handleSubmit,
        control,
        formState: { errors }
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            oxygenGivenDetails: [
                {
                    date: "",
                    startTime: "",
                    endTime: "",
                }
            ]
        }
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "oxygenGivenDetails",
    });

    const fetchIpdEntries = async () => {
        try {
            const response = await GetIpdEntries();
            if (response.success) {
                setIpdEntries(response.data);
            } else {
                showToast("error", response.message);
            }
        } catch (error) {
            showToast("error", error.message);
        }
    };

    useEffect(() => {
        fetchIpdEntries();
    }, []);


    const onSubmit = async data => {
        try {
            dispatch({ type: "LOADER_START" });
            const response = await StoreOxygenGiven(data);

            if (response.success) {
                showToast("success", response.message);
                navigate(`/ipd/transaction/oxygen-given-entry/list`);
            } else {
                showToast("error", response.message);
            }
        } catch (error) {
            showToast("error", error.message);
        } finally {
            dispatch({ type: "LOADER_STOP" });
        }
    };

    const formRef = useRef(null);
    useKeyboardSubmit(handleSubmit, onSubmit);
    useEnterNavigation(formRef);

    return (
        <Fragment>
            <Breadcrumb parent="Oxygen Given" title="Add Oxygen Given Entry" />
            <div className="container-fluid">
                <div className="row">
                    <div className="col-sm-12">
                        <div className="card">
                            <div className="card-header d-flex justify-content-between">
                                <h5>Add Oxygen Given Entry</h5>
                                <Link
                                    to={`${process.env
                                        .PUBLIC_URL}/ipd/transaction/oxygen-given-entry/list`}
                                >
                                    <button className="btn btn-primary" id="list">
                                        List Oxygen Given Entry
                                    </button>
                                    <UncontrolledTooltip placement="top" target="list">
                                        List Oxygen Given Entry
                                    </UncontrolledTooltip>
                                </Link>
                            </div>

                            <form
                                ref={formRef}
                                className="form theme-form"
                                onSubmit={handleSubmit(onSubmit)}
                            >
                                <div className="card-body">

                                    {/* ================= IPD & BASIC ================= */}
                                    <div className="row">
                                        <div className="col-6">
                                            <label>Room No</label>
                                            <select className="form-control" {...register("ipdEntryId", {
                                                onChange: (e) => {
                                                    setPatientDetails(ipdEntries.find(ipd => ipd._id === e.target.value));
                                                }
                                            })}>
                                                <option value="">Select IPD</option>
                                                {ipdEntries.map(ipd => (
                                                    <option key={ipd._id} value={ipd._id}>
                                                        {ipd.roomId?.roomNo}
                                                    </option>
                                                ))}
                                            </select>
                                            {errors.ipdEntryId && <span className="text-danger">{errors.ipdEntryId.message}</span>}
                                        </div>
                                    </div>

                                    <div className="col-12">
                                        <h5 className="mt-3 mb-3 text-primary">
                                            Patient Details
                                        </h5>
                                    </div>
                                    <div className="row">
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>UHID No</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="UHID No"
                                                    value={patientDetails?.patientId?.uhidNo}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Admit Date</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Admit Date"
                                                    value={patientDetails?.date?.split("T")[0]}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Admit time</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Admit time"
                                                    value={patientDetails?.date?.split("T")[1].split(".")[0]}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Ipd No</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Ipd No"
                                                    value={patientDetails?.ipdNo}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Patient</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Patient Name"
                                                    value={patientDetails?.patientId?.patientName}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Guardian Name</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Guardian Name"
                                                    value={patientDetails?.patientId?.guardianName}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Age</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Patient Name"
                                                    value={patientDetails?.patientId?.age}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Case</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Case"
                                                    value={patientDetails?.caseId?.name}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Consultant Doctor</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Consultant Doctor"
                                                    value={patientDetails?.consultantDoctorId?.name}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>ReferenceDoctor</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="ReferenceDoctor"
                                                    value={patientDetails?.referenceDoctorId?.name}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label>Address</label>
                                                <input
                                                    className="form-control"
                                                    placeholder="Address"
                                                    value={patientDetails?.patientId?.address}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <h5 className="text-primary mt-4">Oxygen Given Details</h5>

                                    {fields.map((field, index) => (
                                        <div className="row border p-2 mb-2 col-6" key={field.id}>
                                            <div className="col-4">
                                                <label>Date</label>
                                                <input type="date" className="form-control"
                                                    {...register(`oxygenGivenDetails.${index}.date`)} />
                                                {errors.oxygenGivenDetails?.[index].date && <span className="text-danger">{errors.oxygenGivenDetails?.[index].date.message}</span>}
                                            </div>
                                            <div className="col-4">
                                                <label>Start Time</label>
                                                <input type="time" className="form-control"
                                                    {...register(`oxygenGivenDetails.${index}.startTime`)} />
                                                {errors.oxygenGivenDetails?.[index].startTime && <span className="text-danger">{errors.oxygenGivenDetails?.[index].startTime.message}</span>}
                                            </div>
                                            <div className="col-4">
                                                <label>End Time</label>
                                                <input type="time" className="form-control"
                                                    {...register(`oxygenGivenDetails.${index}.endTime`)} />
                                                {errors.oxygenGivenDetails?.[index].endTime && <span className="text-danger">{errors.oxygenGivenDetails?.[index].endTime.message}</span>}
                                            </div>

                                            <div className="col-12 mt-2 text-end">
                                                <button type="button" className="btn btn-danger btn-sm"
                                                    onClick={() => remove(index)}>
                                                    Remove
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                    <button
                                        type="button"
                                        className="btn btn-secondary mt-2"
                                        onClick={() => append({})}
                                    >
                                        + Add New
                                    </button>
                                </div>

                                <div className="card-footer text-end">
                                    <button type="submit" className="btn btn-primary">
                                        Save
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default AddOxygenGivenEntry;
